import { endOfDay } from "date-fns/endOfDay";
import { startOfDay } from "date-fns/startOfDay";
import { sub } from "date-fns/sub";

/** A date range as stored: ISO strings, because a record has to survive JSON. */
export interface DateRangeSettings extends Record<string, unknown> {
  fromDateIso: string;
  toDateIso: string;
}

/**
 * The range a page starts with: whole days, ending today.
 *
 * @param lookbackDays - How many days back the range reaches from today.
 * @param now - The current time, injectable for tests.
 */
export function defaultDateRangeSettings(lookbackDays: number, now = new Date()): DateRangeSettings {
  return {
    fromDateIso: startOfDay(sub(now, { days: lookbackDays })).toISOString(),
    toDateIso: endOfDay(now).toISOString(),
  };
}

/**
 * Reads a stored timestamp, falling back when it is missing or unusable.
 *
 * A record written by an older version, or edited by hand, must not be able to put a page into
 * an invalid range — every query built from it would fail.
 */
export function toStoredDate(iso: string | undefined, fallback: Date): Date {
  if (!iso) {
    return fallback;
  }
  const parsed = new Date(iso);
  return Number.isFinite(parsed.getTime()) ? parsed : fallback;
}

/** Serialises a date for storage, or `null` if it is not a usable date. */
export function toStoredIso(date: Date): string | null {
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}
